import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Calendar, ArrowUpRight, Tag, Clock, Star, Timer } from "lucide-react";
import { track } from "@/lib/api";
import CardHeart from "@/components/CardHeart";

const FB = "https://images.unsplash.com/photo-1644864528108-23474dbab6c6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
export const img = (u) => u || FB;

// Parse an offer's end date from explicit ends_at or a date-like valid_until
export function offerEnd(o) {
  const raw = o?.ends_at || (/\d{4}-\d{2}-\d{2}/.test(o?.valid_until || "") ? o.valid_until.match(/\d{4}-\d{2}-\d{2}/)[0] : "");
  if (!raw) return null;
  const d = new Date(raw.length <= 10 ? raw + "T23:59:59" : raw);
  return isNaN(d.getTime()) ? null : d;
}

// Live countdown for deals. variant: "pill" (compact) | "full"
export function DealCountdown({ end, variant = "pill" }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!end) return null;
  const diff = end.getTime() - now;
  if (diff <= 0) return variant === "full" ? <span className="text-neutral-500 text-sm">This deal has ended</span> : null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const soon = diff < 3 * 86400000;
  if (variant === "full") {
    const box = (v, l) => (
      <div className="text-center">
        <div className="bg-black/60 border border-[#FF0055]/30 px-3 py-2 min-w-[52px] font-display font-extrabold text-2xl text-[#FF0055] tabular-nums">{String(v).padStart(2, "0")}</div>
        <div className="text-[10px] font-accent uppercase tracking-wider text-neutral-500 mt-1">{l}</div>
      </div>
    );
    return <div className="flex gap-2" data-testid="deal-countdown-full">{box(d, "Days")}{box(h, "Hrs")}{box(m, "Min")}{box(s, "Sec")}</div>;
  }
  if (!soon) return null; // only nudge on the card when it's actually ending soon
  return (
    <span data-testid="deal-ending-soon" className="inline-flex items-center gap-1 bg-[#FF0055] text-white text-[10px] font-accent px-2 py-1 animate-pulse">
      <Timer size={11} /> Ends in {d > 0 ? `${d}d ${h}h` : `${h}h ${m}m`}
    </span>
  );
}

export const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

export function Overline({ children, className = "" }) {
  return <span className={`font-accent text-[#00E5FF] text-sm ${className}`}>{children}</span>;
}

export function BusinessCard({ b, index = 0 }) {
  return (
    <motion.div variants={fadeUp} custom={index} initial="hidden" whileInView="show" viewport={{ once: true }}>
      <Link
        to={`/directory/${b.slug}`}
        data-testid={`business-card-${b.slug}`}
        className="group block border border-white/10 bg-[#141414] hover:border-[#FF0055] transition-colors duration-300 h-full"
      >
        <div className="relative overflow-hidden h-52">
          <img src={img(b.image)} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
          {b.featured && <span className="absolute top-3 left-3 bg-[#FF0055] text-white text-[10px] font-accent px-2 py-1 flex items-center gap-1"><Star size={10} /> Featured</span>}
          {b.status === "closed" && <span className="absolute top-3 right-3 bg-black/80 text-neutral-300 text-[10px] font-accent px-2 py-1">Closed</span>}
          <div className="absolute bottom-3 right-3"><CardHeart kind="venue" subjectKey={b.slug} label={b.name} /></div>
        </div>
        <div className="p-5">
          <span className="font-accent text-[#00E5FF] text-xs">{b.category}</span>
          <h3 className="font-display font-bold text-xl text-white mt-1 group-hover:text-[#FF0055] transition-colors">{b.name}</h3>
          <p className="text-neutral-400 text-sm mt-2 line-clamp-2">{b.tagline || b.description}</p>
          <div className="flex items-center gap-1 text-neutral-500 text-xs mt-4"><MapPin size={13} /> {b.address?.split(",")[0]}</div>
        </div>
      </Link>
    </motion.div>
  );
}

export function EventCard({ e, index = 0 }) {
  const d = e.date ? new Date(e.date + "T00:00:00") : null;
  return (
    <motion.div variants={fadeUp} custom={index} initial="hidden" whileInView="show" viewport={{ once: true }}
      data-testid={`event-card-${e.slug}`}>
      <Link to={`/events/${e.slug}`} onClick={() => track("event", e.id, "click")}
        className="group flex gap-4 border border-white/10 bg-[#141414] p-4 hover:border-[#00E5FF] transition-colors cursor-pointer">
        <div className="relative w-28 h-28 flex-shrink-0 overflow-hidden">
          <img src={img(e.image)} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute top-2 right-2"><CardHeart kind="event" subjectKey={e.slug} label={e.title} /></div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[#FF0055] text-xs font-accent">
            <Calendar size={13} />
            {d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "TBA"}
            <span className="text-neutral-500">• {e.category}</span>
          </div>
          <h3 className="font-display font-bold text-lg text-white mt-1 line-clamp-1 group-hover:text-[#00E5FF] transition-colors">{e.title}</h3>
          <p className="text-neutral-400 text-sm mt-1 line-clamp-2">{e.description}</p>
          <div className="flex items-center gap-3 text-neutral-500 text-xs mt-2">
            <span className="flex items-center gap-1"><Clock size={12} /> {e.time}</span>
            <span className="text-[#FFEA00]">{e.price}</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export function OfferCard({ o, index = 0 }) {
  return (
    <motion.div variants={fadeUp} custom={index} initial="hidden" whileInView="show" viewport={{ once: true }}
      data-testid={`offer-card-${o.id}`}>
      <Link to={`/offers/${o.slug}`} onClick={() => track("offer", o.id, "click")}
        className="group block border border-white/10 bg-[#141414] overflow-hidden hover:border-[#FFEA00] transition-colors h-full">
        <div className="relative h-40 overflow-hidden">
          <img src={img(o.image)} alt={o.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          {o.discount && <span className="absolute top-3 left-3 bg-[#FFEA00] text-black text-xs font-bold px-3 py-1 flex items-center gap-1"><Tag size={12} /> {o.discount}</span>}
          <span className="absolute bottom-3 right-3"><DealCountdown end={offerEnd(o)} /></span>
        </div>
        <div className="p-5">
          <span className="text-neutral-500 text-xs">{o.business_name}</span>
          <h3 className="font-display font-bold text-lg text-white mt-1 group-hover:text-[#FFEA00] transition-colors">{o.title}</h3>
          <p className="text-neutral-400 text-sm mt-2 line-clamp-2">{o.description}</p>
          {o.valid_until && <div className="text-neutral-500 text-xs mt-3">Valid until {o.valid_until}</div>}
        </div>
      </Link>
    </motion.div>
  );
}

export function ArticleCard({ a, index = 0 }) {
  return (
    <motion.div variants={fadeUp} custom={index} initial="hidden" whileInView="show" viewport={{ once: true }}>
      <Link to={`/guides/${a.slug}`} data-testid={`article-card-${a.slug}`}
        className="group block border border-white/10 bg-[#141414] hover:border-[#FF0055] transition-colors h-full">
        <div className="relative h-48 overflow-hidden">
          <img src={img(a.image)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <span className="absolute top-3 left-3 bg-black/70 text-[#00E5FF] text-[10px] font-accent px-2 py-1">{a.category}</span>
        </div>
        <div className="p-5">
          <h3 className="font-display font-bold text-lg text-white group-hover:text-[#FF0055] transition-colors line-clamp-2">{a.title}</h3>
          <p className="text-neutral-400 text-sm mt-2 line-clamp-2">{a.excerpt}</p>
          <div className="flex items-center justify-between mt-4 text-xs text-neutral-500">
            <span>{a.author}</span>
            <span className="flex items-center gap-1 text-[#FF0055]">Read <ArrowUpRight size={13} /></span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export function TourCard({ t, index = 0 }) {
  return (
    <motion.div variants={fadeUp} custom={index} initial="hidden" whileInView="show" viewport={{ once: true }}
      data-testid={`tour-card-${t.slug}`}
      className="group border border-white/10 bg-[#141414] overflow-hidden hover:border-[#00E5FF] transition-colors h-full flex flex-col">
      <Link to={`/tours/${t.slug}`} className="relative h-48 overflow-hidden block">
        <img src={img(t.image)} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <span className="absolute top-3 left-3 bg-[#00E5FF] text-black text-[10px] font-accent px-2 py-1">{t.category}</span>
      </Link>
      <div className="p-5 flex-1 flex flex-col">
        <span className="text-neutral-500 text-xs">{t.operator}</span>
        <Link to={`/tours/${t.slug}`}><h3 className="font-display font-bold text-lg text-white mt-1 group-hover:text-[#00E5FF] transition-colors">{t.title}</h3></Link>
        <p className="text-neutral-400 text-sm mt-2 line-clamp-2 flex-1">{t.description}</p>
        <div className="flex items-center justify-between mt-4">
          <span className="text-[#FFEA00] font-bold">{t.price}</span>
          <span className="text-neutral-500 text-xs flex items-center gap-1"><Clock size={12} /> {t.duration}</span>
        </div>
        <Link to={`/tours/${t.slug}`} data-testid={`tour-view-${t.slug}`}
          className="mt-4 text-center bg-[#FF0055] text-white text-sm font-semibold py-2.5 hover:bg-[#ff2d74] transition-colors">View Tour</Link>
      </div>
    </motion.div>
  );
}
